import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "src/components/ui/Card";
import { Button } from "src/components/ui/Button";
import { Badge } from "src/components/ui/Badge";
import { Pencil } from "lucide-react";
import { Modal } from "src/components/ui/Modal";
import { FormInput } from "src/components/ui/FormInput";
import { StudentExtendedProfileData } from "src/pages/student/Profile";
import { ChipInput } from "src/components/ui/ChipInput";
import { preferredTimes } from "src/constants/preferredTimes";
import { preferredDays } from "src/constants/preferredDays";
import { learningStyles } from "src/constants/learningStyles";
import { communicationPreferences } from "src/constants/communicationPreferences";

interface StudyPreferencesSectionProps {
  profileData: StudentExtendedProfileData;
  editData: Partial<StudentExtendedProfileData>;
  activeSectionEdit: string | null;
  setActiveSectionEdit: (section: string | null) => void;
  updateEditData: (field: string, value: any) => void;
  updateProfile: (data: Partial<StudentExtendedProfileData>) => Promise<void>;
}

const StudyPreferencesSection: React.FC<StudyPreferencesSectionProps> = ({
  profileData,
  editData,
  activeSectionEdit,
  setActiveSectionEdit,
  updateEditData,
  updateProfile
}) => {
  return (
    <>
      {/* Study Preferences Section */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Study Preferences</CardTitle>
          <button
            onClick={() => setActiveSectionEdit('study')}
            className="p-1 rounded-full hover:bg-gray-100 transition-colors"
            aria-label="Edit Study Preferences"
          >
            <Pencil className="h-4 w-4 text-gray-500" />
          </button>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h3 className="text-sm font-medium text-gray-500">Preferred Time:</h3>
              <p className="capitalize mt-1">
                {profileData.studyPreferences?.preferredTime && Array.isArray(profileData.studyPreferences.preferredTime)
                  ? profileData.studyPreferences.preferredTime.map((t) => {
                      const obj = preferredTimes.find(pt => pt.value === t);
                      return obj ? obj.label : t;
                    }).join(', ')
                  : (profileData.studyPreferences?.preferredTime || "Not specified")}
              </p>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-500">Preferred Days:</h3>
              <p className="capitalize mt-1">
                {profileData.studyPreferences?.preferredDays?.length > 0
                  ? profileData.studyPreferences.preferredDays.map((d) => {
                      const obj = preferredDays.find(pd => pd.value === d);
                      return obj ? obj.label : d;
                    }).join(', ')
                  : "No preferred days specified"}
              </p>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-500">Learning Style:</h3>
              <p className="capitalize mt-1">
                {profileData.studyPreferences?.learningStyle && Array.isArray(profileData.studyPreferences.learningStyle)
                  ? profileData.studyPreferences.learningStyle.map((l) => {
                      const obj = learningStyles.find(ls => ls.value === l);
                      return obj ? obj.label : l;
                    }).join(', ')
                  : (profileData.studyPreferences?.learningStyle || "Not specified")}
              </p>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-500">Communication Preference:</h3>
              <p className="capitalize mt-1">
                {profileData.studyPreferences?.communicationPreference && Array.isArray(profileData.studyPreferences.communicationPreference)
                  ? profileData.studyPreferences.communicationPreference.map((c) => {
                      const obj = communicationPreferences.find(cp => cp.value === c);
                      return obj ? obj.label : c;
                    }).join(', ')
                  : (profileData.studyPreferences?.communicationPreference || "Not specified")}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Study Preferences Edit Modal */}
      {activeSectionEdit === 'study' && (
        <Modal
          isOpen={activeSectionEdit === 'study'}
          onClose={() => setActiveSectionEdit(null)}
          title="Edit Study Preferences"
        >
          <div className="space-y-5">
            <ChipInput
              label="Preferred Time"
              value={editData.studyPreferences?.preferredTime || []}
              onChange={(chips) => {
                const values = Array.isArray(chips)
                  ? chips.map((chip) => typeof chip === 'string' ? chip : chip.value)
                  : [];
                updateEditData("studyPreferences", {
                  ...editData.studyPreferences,
                  preferredTime: values
                });
              }}
              options={preferredTimes}
              getOptionLabel={opt => typeof opt === 'string' ? opt : opt.label}
              getOptionValue={opt => typeof opt === 'string' ? opt : opt.value}
              allowCustom={false}
              multi={true}
              placeholder="Select preferred times"
              required={false}
            />
            <ChipInput
              label="Preferred Days"
              value={editData.studyPreferences?.preferredDays || []}
              onChange={(chips) => {
                const values = Array.isArray(chips)
                  ? chips.map((chip) => typeof chip === 'string' ? chip : chip.value)
                  : [];
                updateEditData("studyPreferences", {
                  ...editData.studyPreferences,
                  preferredDays: values
                });
              }}
              options={preferredDays}
              getOptionLabel={opt => typeof opt === 'string' ? opt : opt.label}
              getOptionValue={opt => typeof opt === 'string' ? opt : opt.value}
              allowCustom={false}
              multi={true}
              placeholder="Select preferred days"
              required={false}
            />
            <ChipInput
              label="Learning Style"
              value={editData.studyPreferences?.learningStyle || []}
              onChange={(chips) => {
                const values = Array.isArray(chips)
                  ? chips.map((chip) => typeof chip === 'string' ? chip : chip.value)
                  : [];
                updateEditData("studyPreferences", {
                  ...editData.studyPreferences,
                  learningStyle: values
                });
              }}
              options={learningStyles}
              getOptionLabel={opt => typeof opt === 'string' ? opt : opt.label}
              getOptionValue={opt => typeof opt === 'string' ? opt : opt.value}
              allowCustom={false}
              multi={true}
              placeholder="Select learning styles"
              required={false}
            />
            <ChipInput
              label="Communication Preference"
              value={editData.studyPreferences?.communicationPreference || []}
              onChange={(chips) => {
                const values = Array.isArray(chips)
                  ? chips.map((chip) => typeof chip === 'string' ? chip : chip.value)
                  : [];
                updateEditData("studyPreferences", {
                  ...editData.studyPreferences,
                  communicationPreference: values
                });
              }}
              options={communicationPreferences}
              getOptionLabel={opt => typeof opt === 'string' ? opt : opt.label}
              getOptionValue={opt => typeof opt === 'string' ? opt : opt.value}
              allowCustom={false}
              multi={true}
              placeholder="Select communication preferences"
              required={false}
            />
          </div>
          <div className="flex flex-row w-full gap-2 mt-6 justify-between">
            <Button variant="outline" onClick={() => setActiveSectionEdit(null)} className="w-32">
              Cancel
            </Button>
            <Button onClick={() => {
              updateProfile(editData);
              setActiveSectionEdit(null);
            }} className="w-32">
              OK
            </Button>
          </div>
        </Modal>
      )}
    </>
  );
};

export default StudyPreferencesSection;
